import React, { useState, useEffect } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Spinner,
  Badge,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getTechniciansList } from '../ManageServiceOrders/actions/action';
import Select from 'react-select';
import { createTO, returnTO } from './actions/action';
import { fetchTechnicianInventory } from '../ManageTechnicians/actions/action';
import toast from 'react-hot-toast';

const initialFormState = {
  country: 'ae',
  technicianId: '',
  remark: '',
  items: [], // { id, sku, title, qty, available_qty }
  transferFromCode: 'PURF-TECH',
  transferToCode: 'TP-DCWMS',
  transferType: "1",
  createdBy: "",
};

const ReturnTOOffcanvas = ({ isOpen, toggle }) => {
  const users = JSON.parse(localStorage?.getItem('authUser'));
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialFormState);
  const [skuSearch, setSkuSearch] = useState('');
  const [skuResults, setSkuResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const { technicianInventory, isLoading: isInventoryLoading } = useSelector((state) => state.service);
  const { techniciansList } = useSelector((state) => state.technician);

  // Fetch technicians list once
  useEffect(() => {
    getTechniciansList({ dispatch });
  }, [dispatch]);

  // Reset form when offcanvas opens/closes
  useEffect(() => {
    if (isOpen) {
      setForm(initialFormState);
      setSkuSearch('');
      setSkuResults([]);
    }
  }, [isOpen]);

  // Fetch technician inventory when technician is selected
  useEffect(() => {
    if (form.technicianId) {
      fetchTechnicianInventory({
        data: { 
          technicianId: form.technicianId,
          country: 'ae' 
        },
        dispatch
      });
    }
  }, [form.technicianId, dispatch]);

  const addItemToList = (item) => {
    const existingItem = form.items.find(i => i.sku === item.sku);
    if (existingItem) {
      toast.error('Item already added to the list');
      return;
    } else {
      setForm({
        ...form,
        items: [...form.items, { 
          id: item.id || `item-${Date.now()}`, 
          sku: item.sku, 
          title: item.title, 
          quantity: item.qty, // Capture full quantity by default
          available_qty: item.qty 
        }]
      });
    }
  };

  const handleQtyChange = (id, newQty) => {
    const qty = parseInt(newQty) || 0;
    const newItems = form.items.map(i => {
      if (i.id === id) {
        if (qty > i.available_qty) {
          toast.error(`Quantity cannot exceed available stock (${i.available_qty})`);
          return { ...i, quantity: i.available_qty };
        }
        return { ...i, quantity: qty };
      }
      return i;
    });
    setForm({ ...form, items: newItems });
  };

  const removeItem = (id) => {
    setForm({ ...form, items: form.items.filter(i => i.id !== id) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if(form.items.length === 0){
      toast.error("Please add at least one item");
      return;
    } else if (!form.technicianId) {
      toast.error("Please select a technician");
      return;
    }
    handleSaveItem(form);
  };

  const handleSaveItem = async (data) => {
    setIsLoading(true);
    const payload = {
      ...data,
      createdBy: users?.salesPersonCode,
    };
    
    try {
        const response = await returnTO({ data: payload, dispatch });
        
        if (response?.code === 100) {
            toast.success('Return TO created successfully!');
            toggle();
            setIsLoading(false);
        } else {
            toast.error(response?.message || 'Failed to create Return TO');
            setIsLoading(false);
        }
    } catch (error) {
        console.error("Error creating Return TO:", error);
        toast.error('Something went wrong!');
        setIsLoading(false);
    }
  };

  const handleSearchSKU = (e) => {
    const value = e.target.value;
    setSkuSearch(value);
  };

  // Populate search results (all items if search is empty, filtered otherwise, minus selected items)
  useEffect(() => {
    if (form.technicianId) {
      const allItems = [];
      const invData = technicianInventory?.data?.items || {};
      
      Object.keys(invData).forEach(cat => {
        if (Array.isArray(invData[cat])) {
          allItems.push(...invData[cat]);
        }
      });
      
      // Filter out items already in form.items
      const selectedSKUs = form.items.map(i => i.sku);
      const availableItems = allItems.filter(item => !selectedSKUs.includes(item.sku));
      
      if (skuSearch.trim() === '') {
        setSkuResults(availableItems);
      } else {
        const filtered = availableItems.filter(item => 
          item.sku.toLowerCase().includes(skuSearch.toLowerCase()) || 
          item.title.toLowerCase().includes(skuSearch.toLowerCase())
        );
        setSkuResults(filtered);
      }
    } else {
      setSkuResults([]);
    }
  }, [technicianInventory, skuSearch, form.technicianId, form.items]);

  const technicianOptions = techniciansList?.technicians?.map(tech => ({
    value: tech.emp_id,
    label: `${tech.email}`
  })) || [];

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '600px' }}
      backdrop="static"
    >
      <OffcanvasHeader toggle={toggle}>
        Return Transfer Order (TO)
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup className="mb-4">
            <Label for="technician">Select Technician</Label>
            <Select
              id="technician"
              options={technicianOptions}
              value={technicianOptions.find(opt => opt.value === form.technicianId) || null}
              onChange={(opt) => {
                setForm({ ...form, technicianId: opt ? opt.value : null, items: [] });
                setSkuSearch('');
                setSkuResults([]);
              }}
              placeholder="Select Technician..."
              isSearchable
            />
          </FormGroup>

          {form.technicianId && (
            <div className="mt-4 border-top pt-4">
              <div className="mb-2">
                <h6 className="text-secondary small text-uppercase fw-bold">Available Technician Stock</h6>
                <FormGroup>
                <Input
                  type="text"
                  id="skuSearch"
                  placeholder="Filter by SKU or Title..."
                  value={skuSearch}
                  onChange={handleSearchSKU}
                  autoComplete="off"
                  className="mb-3"
                />
              </FormGroup>
                <div className="list-group shadow-sm" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee' }}>
                  {skuResults.length > 0 ? (
                    skuResults.map((item, index) => (
                      <button
                        key={item.sku || index}
                        type="button"
                        className="list-group-item list-group-item-action py-2 border-start-0 border-end-0"
                        style={{ backgroundColor: '#f0fff4' }}
                        onClick={() => addItemToList(item)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="fw-bold d-block">{item.sku}</span>
                            <span className="small text-muted">{item.title}</span>
                          </div>
                          <Badge color="info" pill className="px-3">Stock: {item.qty}</Badge>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="list-group-item text-center text-muted py-4">
                      {isInventoryLoading ? (
                        <>
                          <Spinner size="sm" color="primary" className="me-2" />
                          Scanning technician inventory...
                        </>
                      ) : (
                        skuSearch ? 'No items match your search' : 'No inventory found for this technician'
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-2">
            <h6 className="border-bottom pb-2">Selected Items</h6>
            <Table size="sm" bordered responsive>
              <thead className="table-light">
                <tr>
                  <th>SKU</th>
                  <th>Title</th>
                  <th style={{ width: '80px' }}>Qty</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.length > 0 ? (
                  form.items.map((item) => (
                    <tr key={item.id}>
                      <td className="align-middle small fw-bold text-nowrap">{item.sku}</td>
                      <td className="align-middle">
                        <span className="small">{item.title}</span>
                      </td>
                      <td>
                        <Input
                          type="number"
                          size="sm"
                          value={item.quantity}
                          min="1"
                          max={item.available_qty}
                          onChange={(e) => handleQtyChange(item.id, e.target.value)}
                        />
                        <div className="text-muted" style={{ fontSize: '10px' }}>Max: {item.available_qty}</div>
                      </td>
                      <td className="text-center">
                        <Button
                          color="link"
                          className="text-danger p-0"
                          onClick={() => removeItem(item.id)}
                        >
                          <i className="fa fa-times-circle"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-3">No items added</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <Row>
            <Col md={6}>
              <FormGroup className="mt-4">
                <Label for="transferFromCode">Transfer From</Label>
                <Input
                  type="text"
                  id="transferFromCode"
                  value={form.transferFromCode}
                  readOnly
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mt-4">
                <Label for="transferToCode">Transfer To</Label>
                <Input
                  type="text"
                  id="transferToCode"
                  value={form.transferToCode}
                  readOnly
                  disabled
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mt-4">
            <Label for="remark">Remarks</Label>
            <Input
              type="textarea"
              id="remark"
              rows="3"
              value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })}
              placeholder="Enter internal remarks..."
            />
          </FormGroup>

          <div className="mt-5 d-flex gap-2">
            <Button color="danger" type="submit" className="flex-grow-1 py-2" disabled={isLoading}>
              {isLoading ? 'Returning TO...' : "Return TO"}
            </Button>
            <Button color="light" type="button" onClick={toggle} className="px-4">
              Cancel
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ReturnTOOffcanvas;
